import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { SiteApp } from "@charisol-spark/website";
import { hasResumableSession, useAuthStore } from "@/stores/authStore";

/**
 * Marketing-site entry (index.html).
 *
 * The whole site lives in @charisol-spark/website, which knows nothing about
 * auth. Reading the persisted session is this shell's job precisely so that
 * package can stay free of app dependencies: it is handed the one fact the
 * public pages need — whether someone is signed in, and on what plan — and
 * uses it to swap the CTA between "Get Started Free" and "Go to Dashboard".
 *
 * Read outside React because it is a one-shot value at boot; the marketing
 * pages never re-render on auth changes.
 *
 * Gated on a session that can still be RESUMED, not on a live one.
 *
 * The store persists the user to localStorage, which survives a tab close.
 * Reading it unconditionally meant a visitor whose session was cleared still
 * got the signed-in CTA. But requiring a live access token is the opposite
 * error and a worse one: that token lives in sessionStorage and dies with the
 * tab, so every ordinary returning visitor — the common case — would be shown
 * a signup pitch while holding a valid refresh token. `hasResumableSession`
 * accepts either token.
 *
 * Nothing identifying is rendered from this value; the site bundle reads only
 * `subscriptionTier`, to mark the plan the visitor is on. It picks a CTA, and
 * a wrong guess costs a redirect to /login, where the app's own guards decide.
 */
const persisted = useAuthStore.getState().user;
const user = hasResumableSession(persisted) ? persisted : null;

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <SiteApp user={user} />
  </StrictMode>
);
